"use client";

import { cn } from "../../../lib/cn";
import {
	RippleButton,
	type SafeRippleButtonProps,
} from "../../material-ui/ripple-button";
import { CtaContinuationCountdownStrip } from "./cta-continuation-countdown-strip";
import {
	CTA_RIPPLE_BUTTON_WHILE_HOVER,
	CTA_RIPPLE_BUTTON_WHILE_TAP,
} from "./cta-ripple-motion";
import type { CtaMaxLines, CtaSize, CtaWidth } from "./primary-cta";
import {
	SECONDARY_CTA_CLASSNAME,
	SECONDARY_CTA_RIPPLE_CLASSNAME,
	SECONDARY_CTA_SLOTTED_CHROME,
} from "./secondary-cta-classname";

export interface SecondaryCtaProps extends SafeRippleButtonProps {
	ctaSize?: CtaSize;
	ctaWidth?: CtaWidth;
	/**
	 * Text wrapping behavior for very narrow containers.
	 * - `1` (default): single line with truncation.
	 * - `2`: clamp to 2 lines, then truncate.
	 */
	maxLines?: CtaMaxLines;
	continuationTimerElapsedPercent?: number;
	continuationTimerShowStrip?: boolean;
}

const SECONDARY_CTA_SIZE_CLASSNAME: Record<
	NonNullable<SecondaryCtaProps["ctaSize"]>,
	string
> = {
	micro: "min-w-0 h-8 rounded-lg px-3 py-0 text-xs font-black uppercase tracking-wide leading-none",
	small: "min-w-0 h-9 rounded-lg px-5 py-0 text-xs font-black uppercase tracking-wide leading-none",
	medium: "min-w-[9rem] h-auto rounded-xl px-4 py-3 text-sm font-black uppercase tracking-wide leading-none",
	// Base class already contains the large sizing tokens.
	large: "",
};

const CTA_WIDTH_CLASSNAME: Record<
	NonNullable<SecondaryCtaProps["ctaWidth"]>,
	string
> = {
	auto: "",
	full: "w-full",
	grow: "flex-1 min-w-0",
};

const CTA_TEXT_MAX_LINES_CLASSNAME: Record<CtaMaxLines, string> = {
	1: "min-w-0 truncate",
	2: "min-w-0 whitespace-normal break-words line-clamp-2 leading-none",
};

/**
 * Secondary CTA (neutral / outline-style) — wraps {@link RippleButton} with shared sizing and motion.
 */
export function SecondaryCta({
	className,
	children,
	type = "button",
	ctaSize = "medium",
	ctaWidth = "auto",
	maxLines = 1,
	appearance = "plain",
	whileHover = CTA_RIPPLE_BUTTON_WHILE_HOVER,
	whileTap = CTA_RIPPLE_BUTTON_WHILE_TAP,
	rippleClassName = SECONDARY_CTA_RIPPLE_CLASSNAME,
	onClick,
	continuationTimerElapsedPercent,
	continuationTimerShowStrip,
	asChild,
	...props
}: SecondaryCtaProps) {
	const label =
		maxLines === 2 &&
		(typeof children === "string" || typeof children === "number") ? (
			<span className={CTA_TEXT_MAX_LINES_CLASSNAME[2]}>{children}</span>
		) : maxLines === 1 &&
			(typeof children === "string" || typeof children === "number") ? (
			<span className={CTA_TEXT_MAX_LINES_CLASSNAME[1]}>{children}</span>
		) : (
			children
		);

	const showContinuationStrip =
		continuationTimerElapsedPercent != null && !asChild;

	return (
		<RippleButton
			{...props}
			asChild={asChild}
			onClick={onClick}
			type={type}
			appearance={appearance}
			variant="ghost"
			className={cn(
				SECONDARY_CTA_CLASSNAME,
				SECONDARY_CTA_SIZE_CLASSNAME[ctaSize],
				CTA_WIDTH_CLASSNAME[ctaWidth],
				Boolean(asChild) && SECONDARY_CTA_SLOTTED_CHROME,
				showContinuationStrip && "overflow-hidden",
				className,
			)}
			rippleClassName={rippleClassName}
			whileHover={whileHover}
			whileTap={whileTap}
		>
			{asChild ? (
				label
			) : showContinuationStrip ? (
				<>
					<CtaContinuationCountdownStrip
						continuationTimerElapsedPercent={
							continuationTimerElapsedPercent
						}
						continuationTimerShowStrip={continuationTimerShowStrip}
					/>
					{label}
				</>
			) : (
				label
			)}
		</RippleButton>
	);
}
